import { Control, Controller, FieldValues, Path } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { PaymentProvider } from "@/components/Checkout/Steps/Checkout/PaymentProvider/PaymentProvider";
import { Link } from "@/components/Interface/Link/Link";
import { Notification } from "@/components/Interface/Notification/Notification";
import { ProfileCard } from "@/components/Layout/BaseLayouts/ProfileCard/ProfileCard";
import { SubscriptionInfoBox } from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionDisplay/SubscriptionInfoBox/SubscriptionInfoBox";
import {
  paymentProviderIcons,
  paymentProviderLabels,
} from "@/components/Portal/Documents/PaymentMethods/PaymentProviderPicker/PaymentProviderPicker";
import { DrCreditCardCardType } from "@/services/model";

export interface PaymentProviderObject extends FieldValues {
  paymentProvider: string;
}

/**
 * Displays the current payment mean - or if none is set, shows a number of radio buttons with payment means to choose from.
 * Layout matches Book Roaming's CheckoutPaymentCard (info tile + Notification hint).
 * Payment method is always mandatory in checkout — no optional/recommended suffix.
 */
export const PaymentMeanSection = <T extends PaymentProviderObject>({
  isInvoice,
  isCreditCard,
  cardType,
  maskedCardNumberString,
  sumIsZero,
  control,
  enableShowCurrent,
}: {
  isInvoice: boolean;
  isCreditCard: boolean | undefined;
  cardType: DrCreditCardCardType | undefined;
  maskedCardNumberString: string;
  sumIsZero: boolean;
  control: Control<T, any>;
  enableShowCurrent: boolean;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const showSaved = enableShowCurrent && !!(isInvoice || isCreditCard);

  return (
    <ProfileCard
      variant="white"
      bodyGap={2}
      dataTestid={showSaved ? "payment-provider-add-device" : undefined}
      title={t("Onboarding.sections.checkout.paymentData")}
      subtitle={
        showSaved
          ? t("Onboarding.sections.checkout.alreadyAddedPaymentData")
          : undefined
      }
    >
      {showSaved ? (
        <>
          <SubscriptionInfoBox className="flex items-center gap-3">
            {isCreditCard && cardType != null && (
              <span className="shrink-0">{paymentProviderIcons[cardType]}</span>
            )}
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <p className="truncate-safe text-body-sm font-semibold leading-none text-foreground-primary">
                {isInvoice
                  ? t("portal:user-account.paymentMethods.invoice")
                  : cardType != null && paymentProviderLabels[cardType]}
              </p>
              {isCreditCard && (
                <p className="truncate-safe text-body-sm leading-none text-foreground-primary">
                  {maskedCardNumberString}
                </p>
              )}
            </div>
          </SubscriptionInfoBox>
          {!isInvoice && (
            <Notification
              dataTestid="payment-mean-section-hint"
              description={
                <Trans
                  i18nKey="portal:cockpit.managementTile.roaming.checkout.paymentMethod.hint"
                  components={[
                    <Link
                      key="settings-link"
                      type="button"
                      size="sm"
                      onClick={() => navigate("/portal/documents/payments")}
                    />,
                  ]}
                />
              }
            />
          )}
        </>
      ) : (
        <Controller
          control={control}
          // This should be guarded by the constraint of the type T
          name={"paymentProvider" as Path<T>}
          render={({ field: { onChange, value } }) => (
            <PaymentProvider
              paymentProvider={value}
              sumIsZero={sumIsZero}
              onChange={onChange}
            />
          )}
        />
      )}
    </ProfileCard>
  );
};
